//
//  BloodPressureRegisteration.viewModel.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { useRef, useEffect, useState, useCallback } from "react";
import { postBloodPressure } from "../network/api";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import KentaroUser from "../domains/user/KentaroUser";
import KentaroBloodPressure from "../domains/bloodPressure/KentaroBloodPressure";
import { ProgressStatus } from "../component/ProgressBar";
import { IDetectedBarcode } from "@yudiel/react-qr-scanner";

// TODO: 使われる側でハードコーディングされているのでenumなどにする
type ERROR_TYPE = "USER" | "BLOOD_PRESSURE" | "OTHER" | null;

// TODO:LOADINGのステータスを削除する方向で検討
const MESSAGES = {
  START: "社員用QRコードをかざしてください",
  SCANE: "血圧測定値QRコードをかざしてください",
};

const ALERT_MESSAGES = {
  USER_SCANED_SUCCESSFUL: "社員用QRコードが読み取れました。",
  BLOOD_PRESSURE_SCANED_SUCCESSFUL:
    "血圧測定値QRコードが読み取れました。履歴画面を表示します。",
};

const ERROR_MESSAGES = {
  USER_SCANED_ERROR:
    "社員用QRコードが読み取れませんでした。社員用QRコードを再度かざしてください。",
  BLOOD_PRESSURE_SCANED_ERROR:
    "血圧測定値QRコードが読み取れませんでした。血圧測定値QRコードを再度かざしてください。",
  ERROR_OTHER: "エラーが発生しました。もう一度お試しください。",
};

const ERROR_CLEAR_TIME = 1000;
const ERROR_DELAY_TIME = 5000;

export const useBloodPressureRegistrationViewModel = () => {
  const theme = useTheme();
  const isIpad = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const videoRef = useRef<HTMLVideoElement>(null);
  const userText = useRef<KentaroUser | null>(null);
  const bloodPressureText = useRef<KentaroBloodPressure | null>(null);

  const [message, setMessage] = useState<string>(MESSAGES.START);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorType, setErrorType] = useState<ERROR_TYPE>(null);

  const [alertMessage, setAlertMessage] = useState<string>("");
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const progressStatus = useRef<ProgressStatus>(ProgressStatus.start);

  const [severity, setSeverity] = useState<"error" | "success">("error");

  // スキャンの待機状態
  const [paused, setPaused] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    const constraints = {
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: "environment",
      },
    };

    if (progressStatus.current !== ProgressStatus.bloodPressureScaned) {
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error("Error accessing the camera: ", err);
        });
    }
  }, [isIpad, progressStatus.current]);

  const registerBloodPressure = useCallback(async () => {
    if (progressStatus.current !== ProgressStatus.bloodPressureScaned) return;

    if (!userText.current || !bloodPressureText.current) return;

    const fitinsightUniqueId = userText.current.id;
    const companyId = userText.current.companyId;
    const measurementDateString = bloodPressureText.current?.dateStr;

    const bloodPressure = {
      fitinsightUniqueId: userText.current.id,
      companyId: userText.current.companyId,
      dateString: bloodPressureText.current.dateStr,
      original: bloodPressureText.current.orginal,
      max: bloodPressureText.current.max,
      min: bloodPressureText.current.min,
      pulse: bloodPressureText.current.pulse,
    };

    const success = await postBloodPressure(bloodPressure);

    if (success) {
      navigate(`/BloodPressureHistory`, {
        state: {
          fitinsightUniqueId,
          companyId,
          measurementDateString,
        },
      });
    } else {
      setErrorType("OTHER");
    }
  }, [userText, bloodPressureText, progressStatus.current]);

  /**
   * 読み取ったQRコードをユーザデータとして登録する
   * @param code
   */
  const registerUser = useCallback(
    (code: string) => {
      try {
        const user = new KentaroUser(code);
        userText.current = user;
        progressStatus.current = ProgressStatus.userScaned;
        return true;
      } catch (e) {
        setErrorType("USER");
        return false;
      }
    },
    [progressStatus.current, progressStatus.current]
  );

  /**
   * 読み取ったQRデータを血圧として登録する
   * @param code
   */
  const registerBP = useCallback(
    (code: string) => {
      try {
        const bloodPressure = new KentaroBloodPressure(code);
        bloodPressureText.current = bloodPressure;
        progressStatus.current = ProgressStatus.bloodPressureScaned;
        return true;
      } catch (e) {
        setErrorType("BLOOD_PRESSURE");
        return false;
      }
    },
    [progressStatus.current, progressStatus.current]
  );

  /**
   * QRコードスキャナーハンドラ
   * @param detectedList デコードされたデータリスト
   */
  const handleScanQrCode = useCallback(
    async (detectedList: IDetectedBarcode[]) => {
      let scanned = false;
      for (let i = 0; i < detectedList.length; i++) {
        const detected = detectedList[i];

        // スキャン済みの場合はループを抜ける
        // NOTE: QRスキャンが連続で登録されないようにするために必要
        if (scanned) break;

        switch (progressStatus.current) {
          case ProgressStatus.start:
            scanned = registerUser(detected.rawValue);
            continue;
          case ProgressStatus.userScaned:
            scanned = registerBP(detected.rawValue);
            continue;
        }
      }

      // NOTE: スキャンに成功した場合、すぐに次のスキャンが始まると対象ではないQRとしてエラーになるので少しまつ
      if (scanned) {
        await pauseScan();
      }
    },
    [progressStatus.current]
  );

  /**
   * はじめからやり直すボタン押下時の処理
   */
  const handleClickRestart = useCallback(() => {
    // 各種スキャン結果をリセットする
    userText.current = null;
    bloodPressureText.current = null;

    // エラーメッセージ状態を初期化
    setErrorType(null);
    setErrorMessage("");

    // アラートメッセージ状態を初期化
    setAlertMessage("");
    setShowAlert(false);

    setSeverity("error");

    // ステータスを初期化
    progressStatus.current = ProgressStatus.start;
  }, [progressStatus.current, userText.current, bloodPressureText.current]);

  /**
   * エラーメッセージ閉じるボタンのハンドラ
   */
  const handleCloseError = useCallback(() => {
    setErrorType(null);
    setErrorMessage("");
  }, [errorMessage, errorType]);

  /**
   * アラートメッセージを閉じるボタンのハンドラ
   */
  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
    setAlertMessage("");
  }, [showAlert]);

  /**
   * スキャンを待機
   */
  const pauseScan = useCallback(async (): Promise<void> => {
    return new Promise((res, _rej) => {
      setPaused(true);
      setTimeout(() => {
        setPaused(false);
        res();
      }, 1000);
    });
  }, []);

  useEffect(() => {
    let message = "";
    let Alertmessage = "";
    switch (progressStatus.current) {
      case ProgressStatus.start:
        console.log("start");
        message = MESSAGES.START;
        break;
      case ProgressStatus.userScaned:
        message = MESSAGES.SCANE;
        Alertmessage = ALERT_MESSAGES.USER_SCANED_SUCCESSFUL;
        bloodPressureText.current = null;
        setShowAlert(true);
        setSeverity("success");
        break;
      case ProgressStatus.bloodPressureScaned:
        message = MESSAGES.SCANE;
        Alertmessage = ALERT_MESSAGES.BLOOD_PRESSURE_SCANED_SUCCESSFUL;
        registerBloodPressure();
        setShowAlert(true);
        setSeverity("success");
        break;
    }
    setMessage(message);
    setAlertMessage(Alertmessage);
  }, [progressStatus.current]);

  const clearMessageAfterDelay = useCallback(
    (clearFunction: () => void, delay: number = ERROR_CLEAR_TIME) => {
      const timer = setTimeout(clearFunction, delay);
      return () => clearTimeout(timer);
    },
    []
  );

  useEffect(() => {
    if (showAlert) {
      return clearMessageAfterDelay(() => {
        setShowAlert(false);
        setAlertMessage("");
      });
    }
  }, [showAlert]);

  useEffect(() => {
    let errorMessage = "";
    switch (errorType) {
      case "USER":
        errorMessage = ERROR_MESSAGES.USER_SCANED_ERROR;
        userText.current = null;
        bloodPressureText.current = null;
        progressStatus.current = ProgressStatus.start;
        break;
      case "BLOOD_PRESSURE":
        errorMessage = ERROR_MESSAGES.BLOOD_PRESSURE_SCANED_ERROR;
        bloodPressureText.current = null;
        progressStatus.current = ProgressStatus.userScaned;
        break;
      case "OTHER":
        errorMessage = ERROR_MESSAGES.ERROR_OTHER;
        userText.current = null;
        bloodPressureText.current = null;
        progressStatus.current = ProgressStatus.start;
        break;
    }
    if (errorMessage) {
      setErrorMessage(errorMessage);
      setTimeout(() => {
        clearMessageAfterDelay(() => {
          // エラーメッセージとエラー状態を初期化する
          setErrorMessage("");
          setErrorType(null);
        });
      }, ERROR_DELAY_TIME);
    }
  }, [errorType, errorMessage]);

  return {
    props: {
      videoRef,
      message,
      errorMessage,
      errorType,
      alertMessage,
      showAlert,
      severity,
      paused,
      progressStatus,
    },
    handlers: {
      handleClickRestart,
      handleScanQrCode,
      handleCloseError,
      handleCloseAlert,
    },
  };
};
